import { useState, useEffect, type FC } from 'react';
import { useLocation } from 'react-router-dom';
import { useCurrentSession, useSessionActions } from '@/stores/gameSessionStore';
import { useRobustUrlParams } from '@/hooks/useRobustUrlParams';

interface StudentDebugPanelProps {
  isVisible: boolean;
  onToggle: () => void;
}

export const StudentDebugPanel: FC<StudentDebugPanelProps> = ({ isVisible, onToggle }) => {
  const location = useLocation();
  const { params: robustParams, diagnostics: urlDiagnostics } = useRobustUrlParams();
  const currentSession = useCurrentSession();
  const { refreshSession } = useSessionActions();
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [pollCount, setPollCount] = useState(0);
  const [manualRefreshCount, setManualRefreshCount] = useState(0);

  const shouldShowDebugUi = import.meta.env.VITE_ENABLE_STUDENT_DEBUG_PANEL === 'true';

  // Track updates
  useEffect(() => {
    if (currentSession) {
      setLastUpdate(new Date());
    }
  }, [currentSession]);

  // Track polling activity
  useEffect(() => {
    const interval = setInterval(() => {
      setPollCount(prev => prev + 1);
    }, 1500); // Match the polling interval

    return () => clearInterval(interval);
  }, []);

  if (!shouldShowDebugUi) {
    return null;
  }

  const sessionId = robustParams.sessionId;
  const playerId = robustParams.playerId;
  const isStudentRoute = !location.pathname.startsWith('/teacher');

  const handleManualRefresh = async () => {
    if (sessionId) {
      try {
        console.log('🔄 Manual refresh triggered by student');
        await refreshSession(sessionId);
        setManualRefreshCount(prev => prev + 1);
      } catch (error) {
        console.error('❌ Manual refresh failed:', error);
      }
    }
  };

  const getStatusColor = () => {
    if (!sessionId) return '#FF6B6B'; // Red - no session
    if (!currentSession) return '#FF9800'; // Orange - session loading
    if (currentSession.id !== sessionId) return '#FF9800'; // Orange - session mismatch
    return '#4CAF50'; // Green - all good
  };

  const getConnectionStatus = () => {
    if (!sessionId) return 'No Session ID';
    if (!currentSession) return 'Loading Session...';
    if (currentSession.id !== sessionId) return 'Session Mismatch';
    return 'Connected';
  };

  if (!isStudentRoute && !isVisible) return null;

  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={onToggle}
        style={{
          position: 'fixed',
          top: '10px',
          right: '10px',
          zIndex: 10000,
          padding: '8px 12px',
          backgroundColor: getStatusColor(),
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          fontSize: '12px',
          fontWeight: 'bold',
          cursor: 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
        }}
      >
        {isVisible ? '🔒 Debug' : '🔍 Debug'}
      </button>

      {/* Debug Panel */}
      {isVisible && (
        <div style={{
          position: 'fixed',
          top: '50px',
          right: '10px',
          width: '350px',
          maxHeight: '70vh',
          overflowY: 'auto',
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          color: 'white',
          padding: '16px',
          borderRadius: '8px',
          fontSize: '12px',
          fontFamily: 'monospace',
          zIndex: 9999,
          boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
          border: `2px solid ${getStatusColor()}`
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '12px',
            paddingBottom: '8px',
            borderBottom: '1px solid #333'
          }}>
            <h3 style={{ margin: 0, fontSize: '14px', color: getStatusColor() }}>
              🎮 Student Debug Panel
            </h3>
            <button
              onClick={onToggle}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                fontSize: '16px',
                cursor: 'pointer'
              }}
            >
              ✕
            </button>
          </div>

          {/* Connection Status */}
          <div style={{ marginBottom: '12px' }}>
            <div style={{
              color: getStatusColor(),
              fontWeight: 'bold',
              marginBottom: '4px'
            }}>
              📡 Connection: {getConnectionStatus()}
            </div>
            {lastUpdate && (
              <div style={{ color: '#888' }}>
                Last Update: {lastUpdate.toLocaleTimeString()}
              </div>
            )}
          </div>

          {/* URL Information */}
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>📍 URL Info:</div>
            <div>Path: {location.pathname}</div>
            <div>Session ID: {sessionId || '❌ Missing'}</div>
            <div>Player ID: {playerId || '❌ Missing'}</div>
            <div>Is Student Route: {isStudentRoute ? '✅' : '❌'}</div>
          </div>

          {/* URL Parsing Diagnostics */}
          <div style={{ marginBottom: '12px' }}>
            <div style={{
              fontWeight: 'bold',
              marginBottom: '4px',
              color: urlDiagnostics.hasDiscrepancy ? '#FF6B6B' : '#4CAF50'
            }}>
              🔍 URL Parsing: {urlDiagnostics.parsingMethod}
              {urlDiagnostics.hasDiscrepancy && ' ⚠️'}
            </div>

            {urlDiagnostics.hasDiscrepancy && (
              <div style={{
                backgroundColor: 'rgba(255, 107, 107, 0.2)',
                padding: '8px',
                borderRadius: '4px',
                marginBottom: '8px'
              }}>
                <div style={{ color: '#FF6B6B', fontSize: '11px' }}>
                  ⚠️ Parsing methods disagree!
                </div>
                <div style={{ fontSize: '10px' }}>
                  React Router: {urlDiagnostics.reactRouterParams.sessionId || 'null'}
                </div>
                <div style={{ fontSize: '10px' }}>
                  Manual: {urlDiagnostics.manualParams.sessionId || 'null'}
                </div>
              </div>
            )}

            <div style={{ fontSize: '10px', color: '#888' }}>
              Raw URL: {urlDiagnostics.rawUrl.length > 50 ?
                urlDiagnostics.rawUrl.substring(0, 50) + '...' :
                urlDiagnostics.rawUrl}
            </div>
            <div style={{ fontSize: '10px', color: '#888' }}>
              Search: {urlDiagnostics.rawSearch || 'empty'}
            </div>
          </div>

          {/* Session Information */}
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>🎯 Session Info:</div>
            {currentSession ? (
              <>
                <div>State: <span style={{ color: '#4CAF50' }}>{currentSession.gameState}</span></div>
                <div>Question: {currentSession.currentQuestionIndex + 1}/{currentSession.questions.length}</div>
                <div>Players: {currentSession.players.length}</div>
                <div>Session ID Match: {currentSession.id === sessionId ? '✅' : '❌'}</div>
              </>
            ) : (
              <div style={{ color: '#FF6B6B' }}>No session loaded</div>
            )}
          </div>

          {/* Polling Status */}
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>🔄 Polling Status:</div>
            <div>Poll Count: {pollCount}</div>
            <div>Manual Refreshes: {manualRefreshCount}</div>
          </div>

          {/* Manual Controls */}
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>🎛️ Manual Controls:</div>
            <button
              onClick={handleManualRefresh}
              disabled={!sessionId}
              style={{
                padding: '6px 12px',
                backgroundColor: sessionId ? '#2196F3' : '#666',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '12px',
                cursor: sessionId ? 'pointer' : 'not-allowed',
                marginRight: '8px'
              }}
            >
              🔄 Force Refresh
            </button>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '6px 12px',
                backgroundColor: '#FF9800',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                fontSize: '12px',
                cursor: 'pointer'
              }}
            >
              🔃 Reload Page
            </button>
          </div>

          {/* Player Information */}
          {currentSession && playerId && (
            <div style={{ marginBottom: '12px' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>👤 Player Status:</div>
              {(() => {
                const player = currentSession.players.find(p => p.id === playerId);
                if (player) {
                  return (
                    <>
                      <div>Name: {player.name}</div>
                      <div>Ready: {player.isReady ? '✅' : '❌'}</div>
                      <div>Score: {player.score || 0}</div>
                    </>
                  );
                } else {
                  return <div style={{ color: '#FF6B6B' }}>Player not found in session</div>;
                }
              })()}
            </div>
          )}

          {/* Raw Session Data */}
          <details style={{ marginTop: '12px' }}>
            <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
              📊 Raw Session Data
            </summary>
            <pre style={{
              marginTop: '8px',
              fontSize: '10px',
              maxHeight: '200px',
              overflowY: 'auto',
              backgroundColor: '#111',
              padding: '8px',
              borderRadius: '4px'
            }}>
              {currentSession ? JSON.stringify(currentSession, null, 2) : 'No session data'}
            </pre>
          </details>
        </div>
      )}
    </>
  );
};

export default StudentDebugPanel;
